
import React, { useEffect, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, XCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fastRecover } from '@/utils/deployment/recoveryUtils';

interface ErrorFallbackProps {
  error: Error | string;
  errorInfo?: React.ErrorInfo;
  resetErrorBoundary?: () => void;
  componentName?: string;
  showHeader?: boolean;
  onBack?: () => void;
  critical?: boolean;
}

/**
 * Simplified ErrorFallback component with better recovery options
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error,
  errorInfo,
  resetErrorBoundary,
  componentName,
  showHeader = true,
  onBack,
  critical = false
}) => {
  const [isRecovering, setIsRecovering] = useState(false);
  const { toast } = useToast();
  
  // Log the error when the fallback is displayed
  useEffect(() => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Error in ${componentName || 'unknown component'}:`, errorMessage);
  }, [error, componentName]);

  // Handle retry with reset
  const handleReset = useCallback(() => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
      
      toast({
        title: "Reset Attempted",
        description: "Component reset attempted. Please wait...",
        duration: 2000
      });
    }
  }, [resetErrorBoundary, toast]);

  // Simple page reload
  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);
  
  // Clean recovery with storage cleanup
  const handleCleanReload = useCallback(() => {
    setIsRecovering(true);
    
    toast({
      title: "Recovery Started",
      description: "Cleaning up data and reloading app...",
      duration: 3000
    });
    
    try {
      // Use fastRecover utility to clean problematic storage
      const removed = fastRecover();
      
      // Mark this as a recovery attempt
      localStorage.setItem('recovery_attempt', new Date().toISOString());
      
      setTimeout(() => {
        // Reload with cache busting parameter
        window.location.href = `${window.location.pathname}?fresh=${Date.now()}`;
      }, 500);
    } catch (e) {
      console.error('Recovery failed:', e);
      // If storage operations fail, just do a regular reload
      window.location.reload();
    }
  }, [toast]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-gradient-to-br from-background to-muted/30">
      {showHeader && (
        <div className="border-b px-4 py-3 flex items-center">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="mr-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="ml-1">Back</span>
            </Button>
          )}
          <div className="flex-1 flex justify-center">
            <span className="text-sm text-muted-foreground">
              An error occurred {componentName ? `in ${componentName}` : ''}
            </span>
          </div>
        </div>
      )}
      
      <div className="flex flex-1 w-full overflow-hidden">
        <div className="w-full flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className={`${critical ? 'bg-destructive/20' : 'bg-destructive/10'} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
              {critical ? 
                <XCircle className="h-8 w-8 text-destructive" /> : 
                <AlertTriangle className="h-8 w-8 text-destructive/80" />
              }
            </div>
            
            <h2 className="text-xl font-bold mb-2">
              {critical ? 'Critical Error' : 'Component Error'}
            </h2>
            
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : String(error)}
            </p>
            
            <div className="space-y-3">
              {resetErrorBoundary && !critical && (
                <Button 
                  variant="outline" 
                  onClick={handleReset}
                  className="w-full"
                >
                  Try Again
                </Button>
              )}
              
              <Button 
                variant="outline" 
                onClick={handleReload} 
                className="flex items-center justify-center gap-2 w-full"
                disabled={isRecovering}
              >
                <RefreshCw className="h-4 w-4" />
                Reload Application
              </Button>
              
              <Button 
                variant={critical ? "destructive" : "default"}
                onClick={handleCleanReload}
                className="flex items-center justify-center gap-2 w-full"
                disabled={isRecovering}
              >
                {isRecovering ? (
                  <>Cleaning up and reloading...</>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    {critical ? 'Emergency Recovery' : 'Clean Reload'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ErrorFallback);
